import { ArrowLeft, Activity, TrendingDown, Info } from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { Progress } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/progress";
import { Badge } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/badge";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const weeklyData = [
  { day: "Mon", deviation: -5 },
  { day: "Tue", deviation: -8 },
  { day: "Wed", deviation: -3 },
  { day: "Thu", deviation: -12 },
  { day: "Fri", deviation: -10 },
  { day: "Sat", deviation: -6 },
  { day: "Sun", deviation: -12 },
];

const HRVDeviation = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="mx-auto max-w-md space-y-4 p-4">
        <div className="flex items-center gap-3 pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/fitness2/inputs")}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">HRV Deviation</h1>
        </div>

        {/* Current Value */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-destructive/10">
              <TrendingDown className="h-7 w-7 text-destructive" />
            </div>
            <div className="flex-1">
              <p className="mb-1 text-sm text-muted-foreground">
                Current Deviation
              </p>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-black text-foreground">
                  −12%
                </span>
                <span className="text-sm text-muted-foreground">
                  vs baseline
                </span>
              </div>
            </div>
            <Badge className="rounded-full border-0 bg-destructive/15 text-xs text-destructive">
              Elevated Load
            </Badge>
          </div>
        </Card>

        {/* Impact Meter */}
        <Card className="rounded-xl border-0 p-4 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold text-foreground">
            Load Impact
          </h3>
          <Progress value={75} className="mb-2 h-3" />
          <div className="flex justify-between text-[10px] text-muted-foreground">
            <span>Low</span>
            <span>Moderate</span>
            <span>High</span>
          </div>
        </Card>

        {/* Weekly Trend */}
        <Card className="rounded-xl border-0 p-4 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold text-foreground">
            Weekly HRV Deviation
          </h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weeklyData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="hsl(var(--border))"
                />
                <XAxis
                  dataKey="day"
                  tick={{ fontSize: 11 }}
                  stroke="hsl(var(--muted-foreground))"
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  stroke="hsl(var(--muted-foreground))"
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: 12,
                    border: "none",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  }}
                  formatter={(value: number) => [`${value}%`, "Deviation"]}
                />
                <Bar
                  dataKey="deviation"
                  fill="hsl(var(--destructive))"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* What This Means */}
        <Card className="rounded-xl border-0 p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <Info className="text-info h-5 w-5" />
            <h3 className="font-semibold text-foreground">What This Means</h3>
          </div>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>
              <strong className="text-foreground">HRV deviation</strong>{" "}
              measures how your current heart rate variability compares to your
              personal baseline. A negative deviation indicates your autonomic
              nervous system is under more stress than usual.
            </p>
            <p>
              <strong className="text-foreground">A −12% deviation</strong>{" "}
              suggests elevated physiological load. This can be caused by poor
              sleep, high training load, illness, or psychological stress.
            </p>
            <p>
              <strong className="text-foreground">Impact on metabolism:</strong>{" "}
              Lower HRV is associated with reduced glucose tolerance, meaning
              your body may process carbohydrates less efficiently today.
            </p>
          </div>
        </Card>

        {/* Tips */}
        <Card className="rounded-xl border-0 p-4 shadow-sm">
          <h3 className="mb-3 font-semibold text-foreground">How to Improve</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Prioritize 7-9 hours of quality sleep tonight</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Practice deep breathing or meditation for 10 minutes</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Avoid high-intensity exercise; opt for light walks</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Limit caffeine and alcohol intake</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default HRVDeviation;
